import { useEffect, useMemo, useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import Loader from '../../components/Loader/Loader';
import { useTable, useSortBy } from 'react-table';
import { WhiteCardContainer } from '../../components';
import FormButton from '../../components/formElements/FormButton';
import { PageContentService } from '../../services/Description';

const Description = () => {
    const [fullList, setFullList] = useState([]);
    const [activePage] = useState('que-es-doctolink');
    const [refresh, setRefresh] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const [dialog, setDialog] = useState({ isOpen: false, mode: '' });

    const [id, setId] = useState('');
    const [uniqueKey, setUniqueKey] = useState('');
    const [fieldName, setFieldName] = useState('');
    const [fieldValue, setFieldValue] = useState('');

    const [error, setError] = useState({
        fieldName: '',
        fieldValue: ''
    });

    useEffect(() => {
        setIsLoading(true);
        PageContentService.getList()
            .then((res) => {
                setFullList(res.data);
                setIsLoading(false);
            })
            .catch(() => setIsLoading(false));
    }, [refresh]);

    // Filter page data

    const pageData = useMemo(() => {
        return fullList.find((item) => item.pageType === activePage) || null;
    }, [fullList, activePage]);

    console.log(pageData, 'pdageData');

    // Prepare table rows

    const rows = useMemo(() => {
        if (!pageData) return [];

        return pageData.fields.map((f) => ({
            col1: f.fieldName,
            col2: f.fieldValue,
            col3: (
                <div className="flex gap-2">
                    <div
                        onClick={() => {
                            console.log('Unique Key:', f.uniqueyKey);
                            openModal('edit', {
                                id: pageData.id,
                                fieldName: f.fieldName,
                                fieldValue: f.fieldValue,
                                uniqueKey: f.uniqueyKey
                            });
                        }}
                        className="text-dl-primary-900 hover:text-dl-accent cursor-pointer"
                    >
                        Editar
                    </div>
                </div>
            )
        }));
    }, [pageData]);

    // Columns
    const columns = useMemo(
        () => [
            { Header: 'Nombre del Campo', accessor: 'col1' },
            { Header: 'Valor del Campo', accessor: 'col2' },
            { Header: 'Acciones', accessor: 'col3' }
        ],
        []
    );

    const tableInstance = useTable({ columns, data: rows }, useSortBy);
    const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows: tableRows } = tableInstance;

    // Modal handlers
    const openModal = (mode, item = null) => {
        setDialog({ isOpen: true, mode });

        if (mode === 'edit' && item) {
            setId(item.id);
            setUniqueKey(item.uniqueKey);
            setFieldName(item.fieldName);
            setFieldValue(item.fieldValue);
        } else {
            setId('');
            setUniqueKey('');
            setFieldName('');
            setFieldValue('');
        }

        setError({ fieldName: '', fieldValue: '' });
    };

    const closeModal = () => {
        setDialog({ isOpen: false, mode: '' });
    };

    // Submit
    const handleSubmit = async () => {
        let hasError = false;
        const newError = { fieldName: '', fieldValue: '' };

        if (!fieldName.trim()) {
            newError.fieldName = 'Requerido';
            hasError = true;
        }
        if (!fieldValue.trim()) {
            newError.fieldValue = 'Requerido';
            hasError = true;
        }

        setError(newError);
        if (hasError || !pageData) return;

        try {
            let updatedFields = [...pageData.fields];

            // ADD
            if (dialog.mode === 'add') {
                updatedFields.push({
                    fieldName,
                    fieldValue,
                    uniqueyKey: crypto.randomUUID()
                });
            }

            // EDIT (uniqueKey based)
            if (dialog.mode === 'edit' && uniqueKey) {
                updatedFields = updatedFields.map((f) =>
                    f.uniqueyKey === uniqueKey ? { ...f, fieldName, fieldValue } : f
                );
            }

            await PageContentService.update({
                id: pageData.id,
                fields: updatedFields
            });

            setRefresh((prev) => !prev);
            closeModal();
        } catch (e) {
            console.error(e);
        }
    };

    if (isLoading) return <Loader />;

    return (
        <WhiteCardContainer>
            <div className="px-4 sm:px-6 lg:px-8 mb-10">
                <div className="mt-6 gap-6 my-4 flex justify-between items-center">
                    <h2 className="text-xl font-semibold">¿Qué es Doctolink?</h2>
                </div>

                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                    <table {...getTableProps()} className="min-w-full divide-y divide-gray-300">
                        <thead className="bg-gray-50">
                            {headerGroups.map((headerGroup) => (
                                <tr {...headerGroup.getHeaderGroupProps()}>
                                    {headerGroup.headers.map((column) => (
                                        <th
                                            {...column.getHeaderProps(column.getSortByToggleProps())}
                                            className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900"
                                        >
                                            <div className="inline-flex items-center">
                                                {column.render('Header')}
                                                {column.isSorted &&
                                                    (column.isSortedDesc ? (
                                                        <ChevronUpIcon className="h-5 w-5" />
                                                    ) : (
                                                        <ChevronDownIcon className="h-5 w-5" />
                                                    ))}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>

                        <tbody {...getTableBodyProps()} className="divide-y divide-gray-200 bg-white">
                            {tableRows.map((row) => {
                                prepareRow(row);
                                return (
                                    <tr {...row.getRowProps()}>
                                        {row.cells.map((cell) => (
                                            <td
                                                {...cell.getCellProps()}
                                                className="px-3 py-4 text-sm text-gray-500 whitespace-normal"
                                            >
                                                {cell.render('Cell')}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ADD / EDIT MODAL */}
            {dialog.isOpen && (
                <Transition appear show as={Fragment}>
                    <Dialog as="div" className="relative z-10" onClose={closeModal}>
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0"
                            enterTo="opacity-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <div className="fixed inset-0 bg-black bg-opacity-25" />
                        </Transition.Child>

                        <div className="fixed inset-0 flex items-center justify-center p-4">
                            <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
                                <Dialog.Title className="text-lg font-medium text-gray-900">
                                    {dialog.mode === 'add' ? 'Añadir Campo' : 'Editar Campo'}
                                </Dialog.Title>

                                <div className="mt-4 space-y-4">
                                    <div>
                                        <label className="text-sm font-medium">Nombre del Campo</label>
                                        <input
                                            value={fieldName}
                                            onChange={(e) => setFieldName(e.target.value)}
                                            className="mt-1 w-full rounded-md border px-3 py-2"
                                        />
                                        {error.fieldName && <p className="text-sm text-red-600">{error.fieldName}</p>}
                                    </div>

                                    <div>
                                        <label className="text-sm font-medium">Valor del Campo</label>
                                        <textarea
                                            rows={3}
                                            value={fieldValue}
                                            onChange={(e) => setFieldValue(e.target.value)}
                                            className="mt-1 w-full rounded-md border px-3 py-2"
                                        />
                                        {error.fieldValue && <p className="text-sm text-red-600">{error.fieldValue}</p>}
                                    </div>

                                    <div className="flex justify-end gap-3">
                                        <FormButton label="Cancelar" onClick={closeModal} />
                                        <FormButton label="Guardar" onClick={handleSubmit} />
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </div>
                    </Dialog>
                </Transition>
            )}
        </WhiteCardContainer>
    );
};

export default Description;
